import { COMPONENTS_CATEGORY, EVENT_LABEL } from "constants/index";
import styles from "./SearchInput.module.scss";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import { Suspense, useState, useRef, useEffect } from "react";
import HamburgurIcon from "@Icons/HamburgurIcon";
import Portal from "@Components/Portal";
import Modal from "@Components/Modal";
import FullPageLoader from "@Components/FullPageLoader";
import DropdownMenu from "@Components/Header/DropDownMenu";
import SearchModalMobile from "@Components/SearchModalMobile";
import LoaderIcon from "@Icons/LoaderIcon";
import SearchIcon from "@Icons/SearchIcon";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useAppPadding from "hooks/useAppPadding";
import useIsApp from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import getCookie from "helpers/getCookie";
import useIsIframe from "hooks/useIsIframe";

type NavSearchInputProps = {
  addAppPadding?: boolean;
  isReferralPage?: boolean;
  isAmberPlusHeader?: boolean;
};

const NavSearchInput = ({
  addAppPadding = true,
  isReferralPage = false,
  isAmberPlusHeader = false,
}: NavSearchInputProps) => {
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const staticWordRef = useRef<HTMLSpanElement>(null);
  const animateContainerRef = useRef<HTMLDivElement>(null);
  const { isWhitelabel, whitelabelColors } = useWhitelabel();
  const isApp = useIsApp();
  const isMiniProgram = useIsMiniProgram();
  const { isIframe } = useIsIframe();
  const { t } = useTranslationHook();

  useEffect(() => {
    if (staticWordRef.current && animateContainerRef.current) {
      const staticWordWidth = staticWordRef.current.offsetWidth;
      animateContainerRef.current.style.setProperty(
        "--static-word-width",
        `${staticWordWidth + 15}px`,
      );
    }
  }, [t]);
  const handleEventEmitter = ({ action, label = "", ...rest }: any) => {
    gtagEventEmitter({
      action,
      category: COMPONENTS_CATEGORY.TOP_NAV,
      label: `${PAGE.SEARCH} ${label}`,
      ...rest,
    });
  };
  const appPadding = useAppPadding();
  const handleWeChatSearchRedirection = () => {
    const userAuthToken = getCookie("user_auth_token") || "";
    // @ts-ignore
    if (window.wx && window.wx.miniProgram) {
      try {
        // Navigate to the mini program
        // @ts-ignore
        window.wx.miniProgram.navigateTo({
          url: `/pages/SearchFeature/SearchFeature?token=${encodeURIComponent(userAuthToken)}`,
        });
      } catch (error) {
        console.error("Failed to navigate to mini program:", error);
      }
    }
  };

  const handleSearchButtonClick = () => {
    if (isMiniProgram) {
      gtagEventEmitter({ action: "search_button_clicked", category: PAGE.SEARCH });
      handleWeChatSearchRedirection();
    } else {
      setShowSearchModal(true);
      gtagEventEmitter({ action: "search_button_clicked", category: PAGE.SEARCH });
    }
  };
  const handleOnHamburger = () => {
    setShowSideMenu(true);
    handleEventEmitter({ action: EVENT_LABEL.MENU_CLICKED });
  };
  return (
    <nav
      className={formatClassNames(styles.searchNav, isWhitelabel && styles.whitelabelBackground)}
    >
      <div
        className={formatClassNames(styles.navInner, isWhitelabel && styles.whitelabelBackground)}
        style={{
          height: `${60 + (addAppPadding ? appPadding : 0)}px`,
          ...(isApp && addAppPadding && { paddingTop: appPadding }),
        }}
      >
        <div
          role="none"
          className={formatClassNames(
            styles.searchInputContainer,
            isWhitelabel && styles.whitelabelBackground,
          )}
          onClick={() => {
            handleSearchButtonClick();
          }}
        >
          <div className={styles.searchInputContainerInner}>
            <div className={styles.searchInputSection}>
              <div className={styles.animateContainer} ref={animateContainerRef}>
                <span className={styles.staticWord} ref={staticWordRef}>
                  {t("home.searchinputhome.searchmodaldesktop.searchbyprefix")}
                </span>
                <span className={styles.animateWords}>
                  {t("home.searchinputhome.searchmodaldesktop.citysuffix")}
                </span>
                <span className={styles.animateWords}>
                  {t("home.searchinputhome.searchmodaldesktop.universitysuffix")}
                </span>
                <span className={styles.animateWords}>
                  {t("home.searchinputhome.searchmodaldesktop.propertysuffix")}
                </span>
                <span className={styles.animateWords}>
                  {t("home.searchinputhome.searchmodaldesktop.cityuniversityorproperty")}
                </span>
              </div>
              <i role="none" className={styles.searchButtonNav}>
                {showSearchModal ? (
                  <LoaderIcon
                    fill={isWhitelabel ? whitelabelColors?.primary_text_color : "#ffffff"}
                    size={16}
                  />
                ) : (
                  <SearchIcon size="12" />
                )}
              </i>
            </div>
          </div>
        </div>
        {!(isApp || isMiniProgram) && !(isWhitelabel && isIframe) && (
          <div className={styles.rightSection}>
            <i className={styles.hamburger} role="none" onClick={handleOnHamburger}>
              <HamburgurIcon />
            </i>
          </div>
        )}
      </div>
      {showSideMenu && !(isWhitelabel && isIframe) && (
        <Portal rootId="forPortal">
          <Modal hideModal={() => setShowSideMenu(false)} size="fullScreen" animation="bottomToTop">
            <Suspense fallback={<FullPageLoader />}>
              <DropdownMenu
                showAppBanner={false}
                handleEventEmitter={handleEventEmitter}
                isReferralPage={isReferralPage}
                isAmberPlusHeader={isAmberPlusHeader}
              />
            </Suspense>
          </Modal>
        </Portal>
      )}
      {showSearchModal && (
        <Portal rootId="forPortal">
          <Modal
            hideModal={() => setShowSearchModal(false)}
            size="fullScreen"
            animation="bottomToTop"
            hideCloseButton
          >
            <Suspense fallback={<FullPageLoader />}>
              <SearchModalMobile setShowSearchModal={setShowSearchModal} />
            </Suspense>
          </Modal>
        </Portal>
      )}
    </nav>
  );
};

export default wrapperHOC(NavSearchInput, {
  componentName: "NavSearchInput-Global",
  showForChina: true,
});
